import { berlinUhrzeit } from "@dj/domain/gemeinsam";
import { useEffect, useState, type ReactNode } from "react";
import { Ansicht } from "../design/Ansicht";
import { TextMitKennungen } from "../design/Kennung";
import { Symbol } from "../design/Symbol";
import { beobachten, eintraege, STANDSYMBOL, uebertragen, type Eintrag } from "./warteschlange";

/**
 * Meldungen (oberflaeche.md §10): je Meldung der Warteschlange ein `article` mit dem Titel (§1.5) und dem
 * Übertragungsstand als Text; abgewiesene mit dem Grund. Sortiert nach Handlungsbedarf (design/README §9.8).
 * Ab der Startansicht geöffnet steht dieselbe Liste in einer Ansicht der Drückjagd mit Zustandsband (§3.1, V-7).
 */
const RANG: Record<Eintrag["stand"], number> = { abgewiesen: 0, "noch nicht übertragen": 1, "übertragen": 2 };

/**
 * Klärung einer abgewiesenen Meldung im `article` (oberflaeche.md §10, z. B. „Stand vor Ort anlegen“: gemessene Position,
 * `textbox` „Standnummer“, `button` „Anlegen“). Registriert vom Kontext, dem der Befehl gehört.
 */
export type Klaerung = (e: Eintrag) => ReactNode;
const klaerungen = new Map<string, Klaerung>();

export function klaerungRegistrieren(befehl: string, k: Klaerung): void {
  klaerungen.set(befehl, k);
}

/**
 * Beitrag eines Kontexts zur Ansicht „Meldungen“ (oberflaeche.md §10: „je Meldung der Warteschlange und je eigener
 * Meldung ein `article`“, §8.2 Ergänzungen): eigene Meldungen, die nicht (mehr) in der Warteschlange liegen, aus einem
 * Lesemodell (`useEintraege`, ein React-Hook) und Bedienelemente im `article` eines Eintrags (`aktionen`). Eine eigene
 * Meldung mit demselben Titel wie ein Eintrag der Warteschlange erscheint nur einmal.
 */
export interface EigeneMeldung {
  meldungsId: string;
  titel: string;
  zeitpunkt: Date | null;
}

export interface Meldungszusatz {
  useEintraege?: () => EigeneMeldung[];
  aktionen?: (titel: string) => ReactNode;
}
const zusaetze: Meldungszusatz[] = [];

export function meldungszusatzRegistrieren(z: Meldungszusatz): void {
  if (!zusaetze.includes(z)) zusaetze.push(z);
}

export function Meldungen() {
  return (
    <Ansicht name="Meldungen" symbol="warteschlange">
      <MeldungenListe />
    </Ansicht>
  );
}

export function MeldungenListe() {
  const [liste, setListe] = useState<Eintrag[]>([]);
  useEffect(() => {
    const lesen = () => void eintraege().then(setListe);
    lesen();
    void uebertragen();
    return beobachten(lesen);
  }, []);
  const eigene = zusaetze.flatMap((z) => z.useEintraege?.() ?? []);
  const sortiert = [...liste].sort((a, b) => RANG[a.stand] - RANG[b.stand] || b.erfasst - a.erfasst);
  const titel = new Set(sortiert.map((e) => e.titel));
  const nurEigene = eigene.filter((m) => !titel.has(m.titel)).sort((a, b) => (b.zeitpunkt?.getTime() ?? 0) - (a.zeitpunkt?.getTime() ?? 0));
  const aktionen = (t: string) => zusaetze.map((z, i) => <div key={i} className="meldung__aktionen raster raster--1">{z.aktionen?.(t)}</div>);
  // Referenz `#s-meldungen`: Bereiche nach Handlungsbedarf (abgewiesen, noch nicht übertragen, übertragen), je Meldung
  // ein Eintrag mit Symbol, Titel (§1.5), Angaben und Übertragungsstand; Ergänzungen im Eintrag
  const bereiche: Array<[Eintrag["stand"], string]> = [["abgewiesen", "Abgewiesen"], ["noch nicht übertragen", "Noch nicht übertragen"], ["übertragen", "Übertragen"]];
  return (
    <>
      {bereiche.map(([stand, kopf]) => {
        const teil = sortiert.filter((e) => e.stand === stand);
        const zusatz = stand === "übertragen" ? nurEigene : [];
        if (!teil.length && !zusatz.length) return null;
        return (
          <section key={stand} className="liste-bereich" role="region" aria-label={kopf}>
            <h2 className="kopf platte">{kopf} · {teil.length + zusatz.length}</h2>
            {teil.map((e) => (
              <article key={e.meldungsId} className="eintrag platte" aria-labelledby={`m-${e.meldungsId}`}>
                <Symbol name={STANDSYMBOL[e.stand]} />
                <h3 className="eintrag__t" id={`m-${e.meldungsId}`}><TextMitKennungen text={e.titel} /></h3>
                <span className="eintrag__meta">
                  <span className="zeitstempel">{berlinUhrzeit(new Date(e.geraetezeit))}</span> · <span className="eintrag__status">{e.stand}</span>
                  {e.grund ? <> <span className="grund kennung">{e.grund}</span></> : null}
                </span>
                {e.stand === "abgewiesen" ? klaerungen.get(e.befehl)?.(e) ?? null : null}
                {aktionen(e.titel)}
              </article>
            ))}
            {zusatz.map((m) => (
              <article key={m.meldungsId} className="eintrag platte" aria-labelledby={`m-${m.meldungsId}`}>
                <Symbol name={STANDSYMBOL["übertragen"]} />
                <h3 className="eintrag__t" id={`m-${m.meldungsId}`}><TextMitKennungen text={m.titel} /></h3>
                <span className="eintrag__meta">
                  {m.zeitpunkt ? <><span className="zeitstempel">{berlinUhrzeit(m.zeitpunkt)}</span> · </> : null}<span className="eintrag__status">übertragen</span>
                </span>
                {aktionen(m.titel)}
              </article>
            ))}
          </section>
        );
      })}
      {sortiert.length === 0 && nurEigene.length === 0 ? <p className="leer platte"><Symbol name="uebertragen" /><span>Keine Meldungen</span></p> : null}
    </>
  );
}
